import type { SalesReportResponse } from "@gioia365/contracts";
import type { Locale, MessageKey } from "@gioia365/i18n";
import { useId, type CSSProperties, type ReactElement } from "react";
import { Segmented } from "../design/components/Segmented";
import { formatCount, formatNumericDate } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { averageText, callsText, dayLongText, gridCellKindOf, gridCellWhatText, hourLabel, hourSpanText, sentenceStart } from "./report-format";

// «Andamento» (piano M7, Task 4; mock-up `trendSection` e `barsHtml` del 17/10/2026): con più giorni una barra per giorno lavorativo,
// con «Chiamate» (in blu quelle con risposta), «Disco prenotate», «Demo effettuate» o «Chiusure»; la linea tratteggiata è la media per
// giorno lavorativo del periodo prima (il totale di prima ÷ i suoi giorni lavorativi) e la frase dice le due medie, con un decimale per
// demo e chiusure. Con un giorno solo le chiamate ora per ora, dalla riga della griglia (`callGrid`), con la pausa e il chiuso detti e a
// righe. Le barre sono in scala: la più alta (o la media, se è più in alto) arriva all'88% del riquadro, così il suo numero ci sta
// sopra; ogni barra ha il numero scritto sopra e la sua frase per lo screen reader, quindi si legge anche senza colore. La metrica
// scelta sta nello stato della pagina (D11) e cambia il grafico senza rileggere.

/** Che cosa conta l'andamento con più giorni. */
export type TrendMetric = "calls" | "discoBooked" | "demoHeld" | "closings";
const TREND_METRICS: readonly TrendMetric[] = ["calls", "discoBooked", "demoHeld", "closings"];

const METRIC_LABELS: Readonly<Record<TrendMetric, MessageKey>> = {
  calls: "reports.trend.metrics.calls",
  discoBooked: "reports.trend.metrics.discoBooked",
  demoHeld: "reports.trend.metrics.demoHeld",
  closings: "reports.trend.metrics.closings",
};

/** «1 disco prenotata», «3 disco prenotate», …: il numero di una barra con la sua parola. */
function metricCountText(t: Translate, locale: Locale, metric: TrendMetric, count: number): string {
  if (metric === "calls") return callsText(t, locale, count);
  const value = formatCount(locale, count);
  switch (metric) {
    case "discoBooked":
      return count === 1 ? t("reports.trend.counts.discoBookedOne") : t("reports.trend.counts.discoBookedMany", { count: value });
    case "demoHeld":
      return count === 1 ? t("reports.trend.counts.demoHeldOne") : t("reports.trend.counts.demoHeldMany", { count: value });
    default:
      return count === 1 ? t("reports.table.closingsOne") : t("reports.table.closingsMany", { count: value });
  }
}

/** Fin dove arriva la barra più alta, in percento del riquadro: sopra resta il posto del suo numero. */
const TOP = 88;

type Point = { key: string; value: number; answered?: number; tick: string; sentence: string; state?: "pause" | "closed" };

/** L'altezza in scala, in percento del riquadro, con un decimale. */
const heightOf = (value: number, max: number) => `${((value / max) * TOP).toFixed(1)}%`;

/**
 * Le barre con il numero sopra, le etichette sotto (tutte le ore, oppure il primo e l'ultimo giorno) e, se c'è, la media tratteggiata;
 * il suo numero sta nella legenda, non sulla linea, dove coprirebbe i numeri delle barre.
 */
function Bars({ label, points, hourly, average }: { label: string; points: readonly Point[]; hourly: boolean; average: number | null }): ReactElement {
  const { locale } = useLocale();
  const max = Math.max(1, average ?? 0, ...points.map((point) => point.value));
  const count = points.length;
  const columns = { "--g-report-bars": count } as CSSProperties;
  // Il primo e l'ultimo giorno occupano un terzo delle colonne ciascuno (almeno due da quattro barre in su), perché una data intera
  // non sta sotto una barra sola.
  const span = count <= 3 ? 1 : Math.max(2, Math.floor(count / 3));
  const first = points[0];
  const last = points.at(-1);
  return (
    <div className="g-report-chart">
      <div className="g-report-bars" style={columns} data-dense={count > 12} role="group" aria-label={label}>
        {points.map((point) => (
          <div key={point.key} className="g-report-bar" role="img" aria-label={point.sentence} title={point.sentence} data-state={point.state}>
            <span className="g-report-bar__value" aria-hidden="true">
              {formatCount(locale, point.value)}
            </span>
            <span className="g-report-bar__stack" style={{ height: heightOf(point.value, max) }}>
              {point.value === 0 ? null : point.answered === undefined ? (
                <span className="g-report-bar__yes" style={{ flexGrow: 1 }} />
              ) : (
                <>
                  <span className="g-report-bar__no" style={{ flexGrow: point.value - point.answered }} />
                  <span className="g-report-bar__yes" style={{ flexGrow: point.answered }} />
                </>
              )}
            </span>
          </div>
        ))}
        {average === null ? null : (
          <div className="g-report-bars__layer" aria-hidden="true">
            <div className="g-report-bars__avg" style={{ bottom: heightOf(average, max) }} />
          </div>
        )}
      </div>
      <div className="g-report-ticks" style={columns} aria-hidden="true">
        {hourly || count === 1 || !first || !last ? (
          points.map((point) => <span key={point.key}>{point.tick}</span>)
        ) : (
          <>
            <span className="g-report-tick--first" style={{ gridColumn: `1 / span ${span}` }}>
              {first.tick}
            </span>
            <span className="g-report-tick--last" style={{ gridColumn: `${count - span + 1} / span ${span}` }}>
              {last.tick}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

type Props = {
  report: SalesReportResponse;
  /** Il periodo scritto, per il nome del grafico. */
  range: string;
  /** La metrica scelta, nello stato della pagina. */
  metric: TrendMetric;
  onMetricChange: (metric: TrendMetric) => void;
};

export function TrendSection({ report, range, metric, onMetricChange }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  const singleDay = report.range.from === report.range.to;

  let chart: ReactElement;
  let summary: string;
  let answeredShown: boolean;
  let averageLabel: string | null = null;
  if (singleDay) {
    // Un giorno solo: le sue ore, dalla riga della griglia (una sola, il giorno della settimana di quel giorno).
    const grid = report.callGrid;
    const row = grid.rows[0];
    if (!row) return null;
    const points: Point[] = row.cells.map((cell, index) => {
      const kind = gridCellKindOf(grid, row, index);
      const what = gridCellWhatText(t, locale, kind, cell.calls, cell.answered, false);
      return {
        key: String(cell.hour),
        value: cell.calls,
        answered: cell.answered,
        tick: hourLabel(grid, cell.hour),
        sentence: sentenceStart(locale, t("reports.hours.bar", { span: hourSpanText(t, grid, cell.hour), what })),
        ...(cell.state === "work" ? {} : { state: cell.state }),
      };
    });
    chart = <Bars label={t("reports.trend.chartHours", { range })} points={points} hourly average={null} />;
    summary = t("reports.trend.hourly");
    answeredShown = true;
  } else {
    // Più giorni: una barra per giorno lavorativo; i giorni chiusi in cui qualcuno ha lavorato stanno in «Giorno per giorno».
    const days = report.days.filter((day) => day.working);
    if (days.length === 0) return null;
    const calls = metric === "calls";
    const points: Point[] = days.map((day) => {
      const value = day.metrics[metric];
      const count = metricCountText(t, locale, metric, value);
      const dayText = dayLongText(t, day.day);
      return {
        key: day.day,
        value,
        ...(calls ? { answered: day.metrics.answered } : {}),
        tick: formatNumericDate("UTC", day.day),
        sentence: calls
          ? t("reports.trend.barAnswered", { day: dayText, count, answered: formatCount(locale, day.metrics.answered) })
          : t("reports.trend.bar", { day: dayText, count }),
      };
    });
    // Le due medie sui totali, per giorno lavorativo: adesso e nel periodo prima.
    const decimals = metric === "demoHeld" || metric === "closings" ? 1 : 0;
    const now = averageText(locale, report.totals.current[metric] / days.length, decimals);
    const before = report.previousWorkingDays > 0 ? report.totals.previous[metric] / report.previousWorkingDays : null;
    averageLabel = before === null ? null : t("reports.trend.average", { value: averageText(locale, before, decimals) });
    chart = <Bars label={t("reports.trend.chartDays", { metric: t(METRIC_LABELS[metric]), range })} points={points} hourly={false} average={before} />;
    summary =
      (before === null ? t("reports.trend.summaryNoBefore", { now }) : t("reports.trend.summary", { now, before: averageText(locale, before, decimals) })) +
      (calls ? t("reports.trend.answeredBlue") : "");
    answeredShown = calls;
  }

  return (
    <section className="g-report-section g-report-panel" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <h2 id={titleId}>{t("reports.trend.title")}</h2>
          <p className="g-report-hint">{summary}</p>
        </div>
        {singleDay ? null : (
          <Segmented
            label={t("reports.trend.metricsLabel")}
            options={TREND_METRICS.map((value) => ({ value, label: t(METRIC_LABELS[value]) }))}
            value={metric}
            onChange={(value) => onMetricChange(value as TrendMetric)}
          />
        )}
      </div>
      {chart}
      <p className="g-report-legend">
        {answeredShown ? (
          <>
            <span>
              <span className="g-report-legend__swatch" aria-hidden="true" />
              {t("reports.trend.notAnswered")}
            </span>
            <span>
              <span className="g-report-legend__swatch" data-series="answered" aria-hidden="true" />
              {t("reports.trend.answered")}
            </span>
          </>
        ) : null}
        {averageLabel === null ? null : (
          <span>
            <span className="g-report-legend__line" aria-hidden="true" />
            {averageLabel}
          </span>
        )}
        <span>{singleDay ? t("reports.trend.legendNoteHourly") : t("reports.trend.legendNote")}</span>
      </p>
    </section>
  );
}
